/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type ReactElement, useMemo } from 'react';
import { Link } from '@/components/link';
import { NmSignInIcon } from '@/components/icons';
import { useAuth } from '@/providers/auth';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';
import { UserMenu } from './user-menu';
import { NmSignInPanel } from './nm-sign-in-panel';
import { useBrand } from '@/lib/brand/use-brand';
import type { Brand } from '@/lib/brand/brand';
import { HEADER_COMPOSITION } from '@/lib/brand/header-composition';
import { accountNavFor } from '@/components/account-navigation';
import { routes } from '@/route-paths';

/**
 * What a GUEST's sign-in control does when it is activated. One keyed row per brand, the
 * same idiom as `MOBILE_BAG` / `MINI_CART_SURFACE` / `DESKTOP_BAG_COUNT` in
 * components/header/cart-badge.tsx — a third brand is a compile error here rather than a
 * silent inheritance of NM's behaviour.
 *
 * THE TWO BRANDS GENUINELY DIVERGE, and both halves are measured from live's own markup:
 *
 *   nm  `<button id="your-neimans-cta" aria-label="Sign In and Register - Your Neiman's
 *        Account Panel">` — no href, opens a right-side account panel. Present in the
 *        1280 guest capture (`.reference/live-shots/owner-2026-08-22/designers-nm-1280.html`,
 *        offset 75145) and in the mobile dump (`.reference/homepage.mobile.html`) as the
 *        SAME node, so live NM is a panel at every width — there is no width gate to model.
 *
 *   bg  `<a class="account-header-menu-item__signin-register-link desktop-only"
 *        href="/e/login">Sign in</a>` — a plain navigation. Two independent dumps agree
 *        (`.reference/bg-homepage.desktop.html:77799`,
 *        `.reference/bg-plp-womens-dresses.desktop.html:81800`).
 *
 * This is NOT a width question and must not be modelled as one. Width is expressed
 * throughout this header by Tailwind responsive classes (`composition.signInMobileGate`,
 * `composition.desktopInline`); a JS width read would feed the hydration mismatch already
 * tracked on these pages.
 */
type GuestSignInAffordance = 'account-panel' | 'login-link';

const GUEST_SIGN_IN: Record<Brand, GuestSignInAffordance> = {
    nm: 'account-panel',
    bg: 'login-link',
};

/**
 * Hover treatment for the sign-in / my-account control, read off each brand's own shipped
 * stylesheet rather than chosen:
 *
 *   nm  `.your-neimans-cta:hover{text-decoration:underline}`
 *       — designers-nm-1280_files/ui-header.e380dd240061b8222c1c.bundle.css
 *   bg  `.account-header-menu-item__signin-register-link:hover{color:#5225a5}` and the
 *       identical rule on `…__loggedin-user-link:hover`, so the value covers the guest and
 *       the registered control alike
 *       — bg-account-dt_files/client.13d04758ed47cf397a03.bundle.css
 *
 * It replaces a shared `hover:opacity-50 transition-opacity` that matched neither brand and
 * was the whole of the owner's "no hover state on either brand's sign-in button". The
 * literal hex is written out rather than templated because Tailwind's scanner cannot see a
 * class it did not read as a literal string; `#5225a5` has no token in our tree, and
 * declaring one would force a shared default on NM that nobody measured.
 *
 * THE `transition-none` ON THE TRIGGER IS PART OF THIS and is deliberate, not left over.
 * `buttonVariants` supplies `transition-all`, which the old `transition-opacity` used to
 * displace through tailwind-merge; dropping the opacity utility let `transition-all` back in
 * unasked. Neither brand's bundle declares a transition on this control — live NM's rule is a
 * bare `text-decoration:underline`, live BG's a bare `color` — so the framework's animation is
 * stripped rather than silently inherited (measurement-discipline §4).
 */
const SIGN_IN_HOVER: Record<Brand, string> = {
    nm: 'hover:underline',
    bg: 'hover:text-[#5225a5]',
};

export default function UserActions(): ReactElement {
    const session = useAuth();
    const { t } = useTranslation('header');
    const { t: tAccount } = useTranslation('account');
    // Per-brand utility presentation — see lib/brand/header-composition.ts.
    // NM is an icon rail with a labelled sign-in; BG's desktop utilities are
    // TEXT LINKS with no glyph at all (bg-home-d-1280/App.tsx:96-98 is an <a>
    // wrapping a bare <span>Sign in</span> — the capture's header contains no
    // <img> or <svg> in that third). The glyph/label pair flips at the SAME
    // width as every other gate because both classes come from the same row.
    // Template strings, not `cn()`: NM's rendered class strings must stay
    // BYTE-IDENTICAL (they are pinned in the storybook snapshots), and
    // tailwind-merge is free to normalise. On NM this suffix is the empty
    // string, so the glyph's class is exactly what it was.
    const brand = useBrand();
    const composition = HEADER_COMPOSITION[brand];
    const glyphGate = composition.desktopUtilities === 'text-links' ? ` ${composition.mobileOnly}` : '';
    // `signInMobileGate` is the separate question of whether THIS control
    // belongs to the mobile header AT ALL. Live BG's mobile right group opens
    // with an empty `<div></div>` (bg-home-m-375/App.tsx:98) and then the bag —
    // there is no sign-in there in glyph OR word form, so no value of
    // `glyphGate` could express it. It leads the class string because this
    // Button declares no display of its own; the gate supplies one, and on NM
    // it is the empty string, leaving the rendered class byte-identical.
    //
    // NM KEEPS ITS SIGN-IN GLYPH AT MOBILE and that is measured, not inherited:
    // home-m-375/App.tsx:54-60 is the `aria-label="Sign In and Register - Your
    // Neiman's Account Panel"` button, and the raw dump renders it as
    // `#your-neimans-cta` at 440. The control that had to go is the wishlist,
    // which is why the old single `utilityMobileGate` became two fields — one
    // answer could not be right for both controls on this brand.
    const isAuthenticated: boolean = useMemo(() => {
        // Check if user is authenticated (has valid token and is registered)
        return Boolean(session?.userType === 'registered' && session?.customerId);
    }, [session]);

    // WHERE "My Account" GOES IS A BRAND FACT, and it used to be the bare
    // literal `'/account/overview'` for both. That is NM's landing and NOT BG's:
    // BG's rail carries no overview row, so a BG shopper clicking the most
    // prominent account control in the header landed on a page with no active
    // rail item. Read it from the rail document instead, where the landing is
    // defined as one of the rail's own rows — see
    // `components/account-navigation/nav-composition.ts`.
    const accountLink = isAuthenticated ? accountNavFor(brand).landing : routes.login;
    const label = isAuthenticated ? tAccount('myAccount') : t('signIn');

    const trigger = (
        // Icons match live rects; framework Button padding stripped — divergences
        // from live are flagged to the owner, never rationalized (see handoff rule 8c).
        // Live "Sign In cta": ~26px row. Inter-icon spacing is the icon row's gap
        // (--header-icon-row-gap) and not button padding — that half of the earlier
        // reading holds, and it is why the wishlist and cart boxes carry none.
        //
        // "ZERO BOX PADDING" WAS A READING OF LIVE'S AUTHOR CSS, AND IT IS ONLY HALF
        // THE STORY. Live really does declare no padding on this control: enumerating
        // every rule in every sheet of live's own shipped bundle that MATCHES this
        // button gives zero author rules for padding-left and zero for padding-right,
        // against a control of two rules supplying font-size 13px on that same
        // element in the same invocation. But live's control is a `button`, and with
        // no author rule the UA default survives — Chrome gives it 1px 6px. Resolved
        // on live's saved page with live's own bundle attached, it computes 6px
        // inline and 1px block. So live AUTHORS no padding and RENDERS 6px, and it
        // is the rendering that paints: our account icon sat 6px right of live's.
        //
        // The inline half is therefore restored for NM, at this element and md+ only,
        // through `--header-signin-box-pad-x` (tokens/brand-nm/header.css, consumed in
        // theme/overrides/header.css). Deliberately NOT through the
        // `--header-icon-box-pad-x` below, which wishlist-icon.tsx also reads and
        // whose icon already matches live. The block half is deliberately not
        // restored either: our icon-row ink top already matches live to the pixel.
        //
        // The focus-visible ring stays functional (it outsets). Mobile (<md) keeps
        // its compact px-1 cluster.
        <Button
            variant="ghost"
            className={`${composition.signInMobileGate} cursor-pointer px-1 md:h-(--header-icon-box-height) md:px-(--header-icon-box-pad-x) md:has-[>svg]:px-(--header-icon-box-pad-x) md:py-(--header-icon-box-pad-y) transition-none hover:bg-transparent ${SIGN_IN_HOVER[brand]}`.trimStart()}
            asChild>
            <Link
                to={accountLink}
                aria-label={label}
                className="md:gap-(--header-signin-label-gap)"
                data-testid="header-user-actions">
                {/* 3c blast-1 (les-blast-1-header-1280.md "Sign In cta"): live
                    row is ~26px tall (content 24) — md+ glyph takes
                    --header-icon-glyph-signin so the icon+label row lands near
                    the live band; mobile keeps 20px, and the Button box keeps
                    its 36px hit target. */}
                <NmSignInIcon className={`size-5 md:size-(--header-icon-glyph-signin)${glyphGate}`} />
                {/* Type role: `labelMediumDefault` (§2.2 — 14px/20px/ls0.16). A
                    secondary control label in the icon rail: one tier below the
                    brand-row gender tabs, sentence case rather than uppercase
                    because the string is a name ("Sign in" / "My Account"), not a
                    silo label. The 8px icon↔label gap is the Button primitive's
                    `gap-2`, which already equals luxe's `--button-item-spacing`.
                    `composition.desktopInline` is NM's own `hidden md:inline`
                    verbatim; on BG it reads
                    `hidden bgnav:inline` — its measured
                    off-scale boundary — so the word appears exactly where the
                    glyph disappears, on either brand. */}
                {/* TRACKING IS ELEMENT-SCOPED, and it is a real divergence, not
                    tidying. The role above supplies ls 0.16px; live's own
                    control declares none. `.your-neimans-cta` in live's header
                    bundle (ui-header.e380dd240061b8222c1c.bundle.css, saved
                    under .reference/live-shots/owner-2026-08-22/
                    designers-nm-1280_files/) sets family, 13px, flex and a 26px
                    height and no letter-spacing at all, and resolving that
                    bundle against live's own markup with JS disabled computes
                    `letter-spacing: normal` on the button. Written as an
                    arbitrary property so the neutral can restate the role's own
                    variable — an unset brand therefore re-asserts exactly what
                    `type-label-medium` already said and cannot move. */}
                <span
                    className={`${composition.desktopInline} type-label-medium md:text-(length:--header-signin-label-size) md:font-(--header-signin-label-weight) md:[letter-spacing:var(--header-signin-label-tracking,var(--typography-label-medium-letter-spacing))] md:[font-family:var(--header-signin-label-family,inherit)] md:translate-y-(--header-signin-label-offset-y)`}>
                    {label}
                </span>
            </Link>
        </Button>
    );

    // REGISTERED shoppers keep the account menu on both brands — live BG's registered
    // control carries a `…__down-arrow-container` and live NM's registered control is the
    // same panel button as its guest one, so neither brand's registered state is what the
    // owner reported and neither is touched here.
    if (isAuthenticated) {
        return <UserMenu isAuthenticated trigger={trigger} />;
    }

    // GUEST. `[ours-extra]` removed on BG: we were rendering a hover popover ("Sign in for
    // the best experience" / Create account) for guests on BOTH brands. Live BG hides its
    // account dropdown for anonymous shoppers outright —
    // `.account-header-menu-item__down-arrow-container--anonymous{display:none!important}`
    // in BG's own bundle — and its guest markup is a bare `<a>` with a `<span>` inside, no
    // menu container at all. So on BG the trigger stands alone and the click navigates,
    // which is exactly what live does.
    if (GUEST_SIGN_IN[brand] === 'account-panel') {
        return <NmSignInPanel trigger={trigger} />;
    }
    return trigger;
}
